/**
 * Construtores de comandos de alto nível.
 *
 * A UI, o compilador de operações de IA e os testes falam por aqui — assim ninguém
 * monta um objeto `Command` na mão e erra um campo.
 */
import { createId } from './ids.js';
import { createTrack } from './factory.js';
import { cmd } from './commands/types.js';
function base(ctx) {
    return {
        sequenceId: ctx.sequenceId,
        source: ctx.source ?? 'USER',
        actorId: ctx.actorId,
        changeId: ctx.changeId,
        reason: ctx.reason,
    };
}
export const build = {
    /**
     * Cria uma track.
     *
     * Recebe a track pronta (e não só o tipo) de propósito: quem constrói precisa do id
     * ANTES de enviar o comando, para já poder endereçar o `ADD_ITEM` que vem junto no
     * mesmo lote. Use `newTrack()` para montá-la.
     */
    addTrack(ctx, track) {
        return cmd({ ...base(ctx), type: 'ADD_TRACK', payload: { track } });
    },
    removeTrack(ctx, trackId) {
        return cmd({ ...base(ctx), type: 'REMOVE_TRACK', payload: { trackId } });
    },
    /** Ids do fundo para o topo. */
    reorderTracks(ctx, order) {
        return cmd({ ...base(ctx), type: 'REORDER_TRACKS', payload: { order } });
    },
    addItem(ctx, trackId, item, mode = 'overwrite') {
        return cmd({ ...base(ctx), type: 'ADD_ITEM', payload: { trackId, item, mode } });
    },
    removeItems(ctx, itemIds, ripple = false) {
        return cmd({ ...base(ctx), type: 'REMOVE_ITEMS', payload: { itemIds, ripple } });
    },
    moveItems(ctx, moves, mode = 'overwrite') {
        return cmd({ ...base(ctx), type: 'MOVE_ITEMS', payload: { moves, mode } });
    },
    trim(ctx, itemId, edge, time, ripple = false) {
        return cmd({ ...base(ctx), type: 'TRIM_ITEM', payload: { itemId, edge, time, ripple } });
    },
    split(ctx, itemId, time, newItemId) {
        return cmd({
            ...base(ctx),
            type: 'SPLIT_ITEM',
            payload: { itemId, time, newItemId: newItemId ?? createId('itm') },
        });
    },
    duplicate(ctx, itemIds, offset) {
        return cmd({ ...base(ctx), type: 'DUPLICATE_ITEMS', payload: { itemIds, offset } });
    },
    /** Remove o trecho e fecha o buraco. É o comando da remoção de silêncio, de fillers
     *  e da edição por transcrição. */
    deleteRange(ctx, start, end, trackIds) {
        return cmd({ ...base(ctx), type: 'DELETE_RANGE', payload: { start, end, trackIds } });
    },
    liftRange(ctx, start, end, trackIds) {
        return cmd({ ...base(ctx), type: 'LIFT_RANGE', payload: { start, end, trackIds } });
    },
    insertGap(ctx, time, duration, trackIds) {
        return cmd({ ...base(ctx), type: 'INSERT_GAP', payload: { time, duration, trackIds } });
    },
    setSpeed(ctx, itemId, speed, opts = {}) {
        return cmd({ ...base(ctx), type: 'SET_SPEED', payload: { itemId, speed, ...opts } });
    },
    setTransform(ctx, itemId, transform) {
        return cmd({ ...base(ctx), type: 'SET_TRANSFORM', payload: { itemId, transform } });
    },
    setCrop(ctx, itemId, crop) {
        return cmd({ ...base(ctx), type: 'SET_CROP', payload: { itemId, crop } });
    },
    setVolume(ctx, itemId, opts) {
        return cmd({ ...base(ctx), type: 'SET_VOLUME', payload: { itemId, ...opts } });
    },
    /** Substitui a trilha inteira de keyframes de uma propriedade — como Smart Zoom e
     *  Auto Reframe entregam seus resultados. */
    setKeyframes(ctx, itemId, property, keyframes) {
        return cmd({ ...base(ctx), type: 'SET_KEYFRAMES', payload: { itemId, property, keyframes } });
    },
    addKeyframe(ctx, itemId, keyframe) {
        return cmd({ ...base(ctx), type: 'ADD_KEYFRAME', payload: { itemId, keyframe } });
    },
    removeKeyframes(ctx, itemId, keyframeIds) {
        return cmd({ ...base(ctx), type: 'REMOVE_KEYFRAMES', payload: { itemId, keyframeIds } });
    },
    updateKeyframe(ctx, itemId, keyframeId, patch) {
        return cmd({ ...base(ctx), type: 'UPDATE_KEYFRAME', payload: { itemId, keyframeId, patch } });
    },
    /** Um keyframe novo com id gerado — o caminho normal de "gravar" pela interface. */
    keyframeAt(property, time, value, easing = 'ease-in-out') {
        return { id: createId('kf'), property, time, value, easing };
    },
    addCaptions(ctx, trackId, items, replace = true) {
        return cmd({ ...base(ctx), type: 'ADD_CAPTIONS', payload: { trackId, items, replace } });
    },
    setCaptionWords(ctx, itemId, words) {
        return cmd({ ...base(ctx), type: 'SET_CAPTION_WORDS', payload: { itemId, words } });
    },
    addText(ctx, trackId, item) {
        return cmd({ ...base(ctx), type: 'ADD_TEXT', payload: { trackId, item } });
    },
    updateText(ctx, itemId, patch) {
        return cmd({ ...base(ctx), type: 'UPDATE_TEXT', payload: { itemId, patch } });
    },
    /** Estilo de legenda. Aceita vários itens porque legenda quase nunca é um bloco só. */
    updateCaptionStyle(ctx, itemIds, patch) {
        return cmd({ ...base(ctx), type: 'UPDATE_CAPTION_STYLE', payload: { itemIds, patch } });
    },
    addMarker(ctx, time, name, color = '#38BDF8') {
        return cmd({
            ...base(ctx),
            type: 'ADD_MARKER',
            payload: { marker: { id: createId('mrk'), time, name, color } },
        });
    },
    updateTrack(ctx, trackId, patch) {
        return cmd({ ...base(ctx), type: 'UPDATE_TRACK', payload: { trackId, patch } });
    },
    /**
     * Agrupa itens.
     *
     * O grupo é uma propriedade dos ITENS (`groupId`), não uma estrutura à parte: um clip
     * agrupado continua sendo um clip comum na track dele. A consequência prática é que
     * agrupar não muda nada no render — é a SELEÇÃO que passa a arrastar os irmãos junto.
     */
    group(ctx, itemIds, groupId) {
        return cmd({
            ...base(ctx),
            type: 'GROUP_ITEMS',
            payload: { itemIds, groupId: groupId ?? createId('grp') },
        });
    },
    ungroup(ctx, groupId) {
        return cmd({ ...base(ctx), type: 'UNGROUP_ITEMS', payload: { groupId } });
    },
    /** Um único passo de undo para N comandos. Base do Auto Edit. */
    batch(ctx, commands) {
        return cmd({ ...base(ctx), type: 'BATCH', payload: { commands } });
    },
};
/**
 * Vários DELETE_RANGE de uma vez.
 *
 * Aplicar da direita para a esquerda é obrigatório: cada remoção desloca tudo que vem
 * depois, então processar em ordem crescente invalidaria os tempos seguintes. Este é
 * o erro mais comum ao implementar remoção de silêncio em lote.
 */
export function buildRangeDeletions(ctx, ranges, trackIds) {
    return [...ranges]
        .sort((a, b) => b.start - a.start)
        .map((r) => cmd({
        ...base(ctx),
        reason: r.reason ?? ctx.reason,
        type: 'DELETE_RANGE',
        payload: { start: r.start, end: r.end, trackIds },
    }));
}
/**
 * Monta uma track nova para um `ADD_TRACK`, com índice acima de todas as existentes.
 *
 * O índice define a ordem de desenho, então empilhar acima das existentes é o que o
 * usuário espera de "mais uma track de música". O nome padrão, porém, usa a posição
 * entre as do MESMO tipo (MUS1, MUS2) — numerar pelo índice global faria a segunda
 * track de música se chamar "MUS7".
 */
export function newTrack(tracks, kind, name) {
    const highest = tracks.reduce((max, t) => Math.max(max, t.index), -1);
    const sameKind = tracks.filter((t) => t.kind === kind).length;
    const track = createTrack({ kind, index: highest + 1, items: [] });
    return { ...track, name: name ?? defaultNameFor(kind, sameKind) };
}
const TRACK_PREFIX = {
    video: 'V',
    image: 'IMG',
    overlay: 'OVL',
    adjustment: 'ADJ',
    text: 'TXT',
    caption: 'CC',
    audio: 'A',
    music: 'MUS',
    sfx: 'SFX',
};
function defaultNameFor(kind, ordinal) {
    return `${TRACK_PREFIX[kind]}${ordinal + 1}`;
}
//# sourceMappingURL=builders.js.map